// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CardHeader } from '@/components/card-header';
import {
  ChecklistRtlOutlined,
  EditOutlined,
  KeyboardReturnOutlined,
  SwapHorizOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { CreateOrderDialog } from '@/order/components/create-order-dialog';
import { Project } from '@/project/api/types';
import {
  FormActions,
  ProjectLineItemsForm,
} from '@/project/components/project-line-items-form';
import { ProjectLineItemsList } from '@/project/components/project-line-items-list';
import { Button, Divider, Paper, Stack, Tooltip } from '@mui/material';
import { Suspense, useCallback, useRef } from 'react';

interface Props {
  project: Pick<
    Project,
    | 'key'
    | 'items'
    | 'defaultLocation'
    | 'locations'
    | 'customer'
    | 'startOn'
    | 'endOn'
  >;
  activeLineItemKey?: string;
  focusChargeKey?: string;
  onSelectLineItem?: (lineItemKey: string | null) => void;
  onFocusChargeComplete?: () => void;
}

export function ProjectLineItemsCard(props: Props) {
  const editor = useDisclosure();
  const fulfillmentDialog = useDisclosure();
  const returnDialog = useDisclosure();
  const exchangeDialog = useDisclosure();
  const formActionsRef = useRef<FormActions | null>(null);

  const handleFormReady = useCallback((actions: FormActions) => {
    formActionsRef.current = actions;
  }, []);

  const hasLineItems = (props.project.items ?? []).length > 0;
  const disabledOrderTooltip =
    'Orders are disabled for projects without any line items. Please add line items to enable fulfillment orders, return orders, and exchanges.';

  return (
    <Paper variant="outlined">
      <CardHeader
        title="Products requested for this project"
        variant="subtitle2"
      >
        {editor.isOpen ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              onClick={() => formActionsRef.current?.cancel()}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => formActionsRef.current?.submit()}
            >
              Save
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip arrow title={!hasLineItems ? disabledOrderTooltip : ''}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={!hasLineItems}
                  startIcon={<ChecklistRtlOutlined fontSize="small" />}
                  onClick={fulfillmentDialog.open}
                >
                  Fulfill
                </Button>
              </span>
            </Tooltip>
            <Tooltip arrow title={!hasLineItems ? disabledOrderTooltip : ''}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={!hasLineItems}
                  startIcon={<KeyboardReturnOutlined fontSize="small" />}
                  onClick={returnDialog.open}
                >
                  Return
                </Button>
              </span>
            </Tooltip>
            <Tooltip arrow title={!hasLineItems ? disabledOrderTooltip : ''}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={!hasLineItems}
                  startIcon={<SwapHorizOutlined fontSize="small" />}
                  onClick={exchangeDialog.open}
                >
                  Exchange
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<EditOutlined fontSize="small" />}
              onClick={editor.open}
            >
              Edit
            </Button>
          </Stack>
        )}
      </CardHeader>
      <Divider />
      {editor.isOpen ? (
        <Suspense>
          <ProjectLineItemsForm
            padding={2}
            project={props.project}
            onCancel={editor.close}
            onSuccess={editor.close}
            onFormReady={handleFormReady}
          />
        </Suspense>
      ) : (
        <ProjectLineItemsList
          padding={2}
          projectKey={props.project.key}
          lineItems={props.project.items}
          projectStart={props.project.startOn}
          projectEnd={props.project.endOn}
          activeLineItemKey={props.activeLineItemKey}
          focusChargeKey={props.focusChargeKey}
          onSelectLineItem={props.onSelectLineItem}
          onFocusChargeComplete={props.onFocusChargeComplete}
        />
      )}
      <CreateOrderDialog
        isOpen={fulfillmentDialog.isOpen}
        onClose={fulfillmentDialog.close}
        initialProjectID={props.project.key}
        defaultOrderType="fulfill"
        disableProjectChange
      />
      <CreateOrderDialog
        isOpen={returnDialog.isOpen}
        onClose={returnDialog.close}
        initialProjectID={props.project.key}
        defaultOrderType="return"
      />
      <CreateOrderDialog
        isOpen={exchangeDialog.isOpen}
        onClose={exchangeDialog.close}
        initialProjectID={props.project.key}
        defaultOrderType="exchange"
      />
    </Paper>
  );
}
